import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getVocabularyTermChildren, type VocabularyDisplayDto } from '@/api/vocabularies';
import { cn } from '@/lib/utils';

import { TreeRow } from './TreeRow';

/** Cache children responses across the dialog session (and across the
 *  leaf-detection lookahead + the real expand call — same query key). */
export const CHILDREN_STALE_MS = 5 * 60_000;
/** Visual indent per tree depth; matches the chevron column width below. */
export const INDENT_PX = 18;
/** Width of the expand-button column (`h-3.5` icon + `p-0.5` padding). Used
 *  as the spacer width on leaf rows so titles line up across siblings. */
export const CHEVRON_COL_PX = 22;

interface HpoTreeProps {
  /** Root id whose children make up the top level of the visible tree. */
  readonly rootId: string;
  readonly display: VocabularyDisplayDto | null;
  readonly excludedHpoIds: ReadonlySet<string>;
  readonly onAddTerm: (term: { id: string; label: string }) => void;
}

/** Entry point — the children of `rootId` are the visible top-level rows. */
export function HpoTree({
  rootId,
  display,
  excludedHpoIds,
  onAddTerm,
}: HpoTreeProps): ReactElement {
  return (
    <Branch
      parentId={rootId}
      depth={0}
      display={display}
      excludedHpoIds={excludedHpoIds}
      onAddTerm={onAddTerm}
    />
  );
}

interface BranchProps {
  readonly parentId: string;
  readonly depth: number;
  readonly display: VocabularyDisplayDto | null;
  readonly excludedHpoIds: ReadonlySet<string>;
  readonly onAddTerm: (term: { id: string; label: string }) => void;
}

/**
 * Fetches the immediate children of `parentId` and renders one row per
 * child. Deeper levels mount only when the user expands their parent row
 * (via `TreeRow`), so we don't eagerly fan out across the whole HPO
 * ontology.
 */
export function Branch({
  parentId,
  depth,
  display,
  excludedHpoIds,
  onAddTerm,
}: BranchProps): ReactElement | null {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['vocabulary', 'hpo', 'children', parentId],
    queryFn: ({ signal }) => getVocabularyTermChildren('hpo', parentId, signal),
    staleTime: CHILDREN_STALE_MS,
  });

  if (query.isPending) {
    return (
      <BranchMessage depth={depth} tone="muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {t('patient.phenotypes.browse.loading')}
      </BranchMessage>
    );
  }
  if (query.isError) {
    return (
      <BranchMessage depth={depth} tone="error">
        {t('patient.phenotypes.browse.error')}
      </BranchMessage>
    );
  }
  // Empty branches render nothing — leaf detection happens upstream in
  // `TreeRow` (PTV2-1167), so an empty result here means the user clicked
  // a node whose lookahead hadn't yet resolved. Silently collapse.
  if (query.data.length === 0) return null;

  return (
    <div>
      {query.data.map((child) => (
        <TreeRow
          key={child.id}
          node={child}
          depth={depth}
          display={display}
          excludedHpoIds={excludedHpoIds}
          onAddTerm={onAddTerm}
        />
      ))}
    </div>
  );
}

interface BranchMessageProps {
  readonly depth: number;
  readonly tone: 'muted' | 'error';
  readonly children: ReactNode;
}

function BranchMessage({ depth, tone, children }: BranchMessageProps): ReactElement {
  return (
    <p
      className={cn(
        'flex items-center gap-2 px-3 py-2 text-xs',
        tone === 'muted' ? 'text-muted-foreground' : 'text-destructive',
      )}
      style={{ paddingLeft: `${String(12 + depth * INDENT_PX)}px` }}
      role={tone === 'error' ? 'alert' : undefined}
    >
      {children}
    </p>
  );
}
